import React, { useState, useEffect, useRef } from 'react';
import { keyframes } from '@emotion/react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Container,
  FormControlLabel,
  Switch,
  Chip,
  Grid,
  Checkbox,
  Link,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Visibility, VisibilityOff, Lock, Person, Cloud, Language, Close } from '@mui/icons-material';
import SidebarLanguageMenu from './SidebarLanguageMenu';
import MfaDialog from './MfaDialog';
import { useWebSocket } from './WebSocketContext';
import { apiRequest, clearApiSessionToken, setApiSessionToken } from '../Utils/apiClient';
import { isInvalidSessionError } from '../Utils/sessionNotice';
import { afterSaasLoginPath } from './SaasAccessPage';
import { useTranslation } from 'react-i18next';

const NETWORK_EDGES = [
  { id: 'start-a', from: 'start', to: 'a', d: 'M 20 340 C 62 320, 98 264, 154 222' },
  { id: 'a-b', from: 'a', to: 'b', d: 'M 154 222 C 188 202, 202 148, 242 126' },
  { id: 'a-c', from: 'a', to: 'c', d: 'M 154 222 C 210 250, 250 286, 298 300' },
  { id: 'b-d', from: 'b', to: 'd', d: 'M 242 126 C 298 96, 360 118, 420 82' },
  { id: 'b-e', from: 'b', to: 'e', d: 'M 242 126 C 292 154, 328 198, 356 228' },
  { id: 'c-e', from: 'c', to: 'e', d: 'M 298 300 C 332 286, 340 252, 356 228' },
  { id: 'c-a', from: 'c', to: 'a', d: 'M 298 300 C 242 342, 178 332, 154 222' },
  { id: 'd-f', from: 'd', to: 'f', d: 'M 420 82 C 450 106, 486 142, 500 196' },
  { id: 'd-b', from: 'd', to: 'b', d: 'M 420 82 C 360 46, 290 66, 242 126' },
  { id: 'e-g', from: 'e', to: 'g', d: 'M 356 228 C 396 252, 452 282, 494 326' },
  { id: 'e-a', from: 'e', to: 'a', d: 'M 356 228 C 304 214, 222 190, 154 222' },
  { id: 'f-c', from: 'f', to: 'c', d: 'M 500 196 C 460 250, 388 286, 298 300' },
  { id: 'g-start', from: 'g', to: 'start', d: 'M 494 326 C 390 374, 148 380, 20 340' },
];

const NETWORK_EDGES_BY_FROM = NETWORK_EDGES.reduce((groups, edge) => {
  groups[edge.from] = groups[edge.from] || [];
  groups[edge.from].push(edge);
  return groups;
}, {});

const CHECKPOINT_POSITIONS = {
  a: { left: '7%', top: '52%' },
  b: { left: '7%', top: '12%' },
  c: { left: '7%', top: '70%' },
  d: { left: '64%', top: '12%' },
  e: { left: '69%', top: '42%' },
  f: { left: '68%', top: '28%' },
  g: { left: '65%', top: '70%' },
};

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionValidationFailed, setSessionValidationFailed] = useState(false);
  const { ws, isUserLoggedIn, serverUrl, setServerUrl, clearCustomServer, isConnected, connect, sessionExpired } = useWebSocket();
  const sessionCheckRequestedRef = useRef(false);
  const networkCameraRef = useRef(null);
  const activeNetworkPathRef = useRef(null);
  const activeThreadDotRef = useRef(null);
  const activeThreadGlowRef = useRef(null);
  const completedNetworkPathRefs = useRef({});
  const branchPreviewPathRefs = useRef({});
  // Custom server inputs
  const [useCustomServer, setUseCustomServer] = useState(() => !!localStorage.getItem('dataunfold_ws_url'));
  const [customServerInput, setCustomServerInput] = useState(() => localStorage.getItem('dataunfold_ws_url') || '');
  const [serverError, setServerError] = useState('');
  const [isConnectingServer, setIsConnectingServer] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(false);
  const [persistServer, setPersistServer] = useState(() => !!localStorage.getItem('dataunfold_ws_url'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loginNoticeReason = sessionExpired || sessionValidationFailed ? 'session_expired' : searchParams.get('reason');
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const { t, i18n } = useTranslation();
  const [languageAnchor, setLanguageAnchor] = useState(null);
  const [activeCheckpoint, setActiveCheckpoint] = useState(null);

  const isTargetServerCurrent = !!(
    customServerInput && serverUrl && (serverUrl.includes(customServerInput) || customServerInput.includes(serverUrl))
  );

  // Redirect if already logged in
  useEffect(() => {
    if (!isUserLoggedIn) return undefined;
    let active = true;
    afterSaasLoginPath().then(path => { if (active) navigate(path); });
    return () => { active = false; };
  }, [isUserLoggedIn, navigate]);

  // If a session token exists, validate it and redirect to home when valid.
  useEffect(() => {
    if (isUserLoggedIn || sessionExpired) {
      return;
    }

    const token = localStorage.getItem('dataunfold_session_token');
    if (!token || sessionCheckRequestedRef.current) {
      return;
    }

    sessionCheckRequestedRef.current = true;
    (async () => {
      try {
        const result = await apiRequest('/api/v1/auth/validate-session', {
          method: 'POST',
          body: JSON.stringify({ session_token: token })
        });

        if (result?.success) {
          clearApiSessionToken();
          setApiSessionToken(token);
          localStorage.setItem('dataunfold_logged_in', 'true');
          navigate(await afterSaasLoginPath());
          return;
        }

        if (!result?.success) {
          console.warn('Session validation returned no confirmation; keeping the saved session.');
        }
      } catch (err) {
        if (isInvalidSessionError(err)) {
          clearApiSessionToken();
          localStorage.setItem('dataunfold_logged_in', 'false');
          setSessionValidationFailed(true);
        } else {
          console.warn('Unable to validate the saved session; keeping it for a later retry.', err);
        }
      } finally {
        sessionCheckRequestedRef.current = false;
      }
    })();
  }, [isUserLoggedIn, sessionExpired, navigate]);

  // Clear connecting indicator when provider reports connected
  useEffect(() => {
    if (isConnectingServer && isConnected) {
      setIsConnectingServer(false);
    }
  }, [isConnectingServer, isConnected]);

  const applyCustomServer = (persist = undefined) => {
    if (!customServerInput || !customServerInput.trim()) {
      setServerError(t('login.enter_websocket_url'));
      return;
    }
    setServerError('');
    setIsConnectingServer(true);
    const persistValue = persist === undefined ? persistServer : !!persist;
    setServerUrl(customServerInput, persistValue);
  };

  const openLanguageMenu = (event) => setLanguageAnchor(event.currentTarget);
  const closeLanguageMenu = () => setLanguageAnchor(null);
  const handleLanguageChange = (lang) => {
    try { i18n.changeLanguage(lang); } catch (e) { /* ignore */ }
    closeLanguageMenu();
  };

  useEffect(() => {
    if (isSmall) {
      setActiveCheckpoint(null);
      return undefined;
    }

    const camera = networkCameraRef.current;
    const activePath = activeNetworkPathRef.current;
    const dot = activeThreadDotRef.current;
    const glow = activeThreadGlowRef.current;
    if (!camera || !activePath || !dot || !glow) {
      return undefined;
    }

    const edgeById = NETWORK_EDGES.reduce((edges, edge) => {
      edges[edge.id] = edge;
      return edges;
    }, {});
    const edgeDuration = 2200;
    const branchPause = 520;
    let animationFrameId;
    let branchTimerId;
    let cancelled = false;
    let currentEdge = edgeById['start-a'];
    let currentEdgeLength = 0;
    let edgeStartTime = 0;
    let checkpointSequence = 0;

    const setDotPosition = (point) => {
      dot.setAttribute('cx', point.x);
      dot.setAttribute('cy', point.y);
      glow.setAttribute('cx', point.x);
      glow.setAttribute('cy', point.y);

      // Move the scene a little toward the dot so the illustration behaves
      // like a camera following the active thread.
      const translateX = Math.max(-28, Math.min(28, (260 - point.x) * 0.16));
      const translateY = Math.max(-20, Math.min(20, (200 - point.y) * 0.14));
      camera.setAttribute(
        'transform',
        `translate(${translateX} ${translateY}) translate(260 200) scale(1.08) translate(-260 -200)`,
      );
    };

    const hideBranchPreviews = () => {
      Object.values(branchPreviewPathRefs.current).forEach((path) => {
        if (path) {
          path.style.opacity = '0';
        }
      });
    };

    const showCheckpoint = (nodeId) => {
      const position = CHECKPOINT_POSITIONS[nodeId];
      if (!position) {
        return;
      }
      const isDecision = ['b', 'd', 'f'].includes(nodeId);
      setActiveCheckpoint({
        ...position,
        key: `${nodeId}-${checkpointSequence}`,
        label: t(isDecision ? 'login.ai_decision' : 'login.ai_work_done'),
        detail: t(isDecision ? 'login.ai_decision_detail' : 'login.ai_work_done_detail'),
      });
      checkpointSequence += 1;
    };

    const chooseNextEdge = (nodeId) => {
      const candidates = NETWORK_EDGES_BY_FROM[nodeId] || [];
      if (!candidates.length) {
        return edgeById['start-a'];
      }

      const forwardCandidates = candidates.filter((edge) => edge.to !== currentEdge.from);
      const choices = forwardCandidates.length ? forwardCandidates : candidates;
      return choices[Math.floor(Math.random() * choices.length)];
    };

    const startEdge = (edge, startTime) => {
      currentEdge = edge;
      activePath.setAttribute('d', edge.d);
      currentEdgeLength = activePath.getTotalLength();
      activePath.style.strokeDasharray = `${currentEdgeLength} ${currentEdgeLength}`;
      activePath.style.strokeDashoffset = `${currentEdgeLength}`;
      activePath.style.opacity = '1';
      edgeStartTime = startTime;
      setDotPosition(activePath.getPointAtLength(0));
    };

    const finishEdge = (edge) => {
      const completedPath = completedNetworkPathRefs.current[edge.id];
      if (completedPath) {
        completedPath.setAttribute('d', edge.d);
        completedPath.style.opacity = '0.34';
      }
      activePath.style.opacity = '0';
    };

    const pauseAtJunction = (edge) => {
      const nextEdges = NETWORK_EDGES_BY_FROM[edge.to] || [];
      if (!nextEdges.length) {
        startEdge(edgeById['start-a'], performance.now());
        animationFrameId = window.requestAnimationFrame(animate);
        return;
      }

      const nextEdge = chooseNextEdge(edge.to);
      nextEdges.forEach((candidate) => {
        const previewPath = branchPreviewPathRefs.current[candidate.id];
        if (!previewPath) {
          return;
        }
        previewPath.setAttribute('d', candidate.d);
        previewPath.style.strokeDasharray = '5 7';
        previewPath.style.strokeDashoffset = '0';
        previewPath.style.opacity = '0.28';
      });
      showCheckpoint(edge.to);

      branchTimerId = window.setTimeout(() => {
        if (cancelled) {
          return;
        }
        hideBranchPreviews();
        startEdge(nextEdge, performance.now());
        animationFrameId = window.requestAnimationFrame(animate);
      }, branchPause);
    };

    const animate = (now) => {
      if (cancelled) {
        return;
      }

      const progress = Math.min(1, (now - edgeStartTime) / edgeDuration);
      const point = activePath.getPointAtLength(currentEdgeLength * progress);
      activePath.style.strokeDashoffset = `${currentEdgeLength * (1 - progress)}`;
      setDotPosition(point);

      if (progress >= 1) {
        finishEdge(currentEdge);
        pauseAtJunction(currentEdge);
        return;
      }

      animationFrameId = window.requestAnimationFrame(animate);
    };

    hideBranchPreviews();
    if (prefersReducedMotion) {
      const initialEdge = edgeById['start-a'];
      activePath.setAttribute('d', initialEdge.d);
      activePath.style.strokeDasharray = 'none';
      activePath.style.strokeDashoffset = '0';
      activePath.style.opacity = '0.72';
      setDotPosition(activePath.getPointAtLength(0));
      setActiveCheckpoint(null);
      return undefined;
    }

    setActiveCheckpoint(null);
    startEdge(currentEdge, performance.now());
    animationFrameId = window.requestAnimationFrame(animate);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(branchTimerId);
      hideBranchPreviews();
      camera.removeAttribute('transform');
    };
  }, [isSmall, prefersReducedMotion, t]);



  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) {
      setError(t('login.enter_both_username_password'));
      return;
    }
    // MFA will be handled in a dedicated dialog when required by the server

    setIsLoading(true);
    setError('');

    return (async () => {
      try {
        if (useCustomServer && customServerInput && customServerInput.trim()) {
          setServerError('');
          setIsConnectingServer(true);
          applyCustomServer(persistServer);
        }

        const response = await apiRequest('/api/v1/auth/login', {
          method: 'POST',
          body: JSON.stringify({
            username: username.trim(),
            password
          })
        });

        if (response?.success) {
          setUsername('');
          setPassword('');
          setMfaRequired(false);
          setError('');
          if (response.session_token) {
            setApiSessionToken(response.session_token);
          }
          localStorage.setItem('dataunfold_logged_in', 'true');
          if (response.mfa_setup_required) {
            localStorage.setItem('dataunfold_mfa_setup_required', 'true');
          } else {
            localStorage.removeItem('dataunfold_mfa_setup_required');
          }
          window.location.href = await afterSaasLoginPath();
          return;
        }

        if (response?.mfa_required) {
          setMfaRequired(true);
          const serverMsg = response?.message || '';
          if (serverMsg && serverMsg !== 'MFA code required' && serverMsg !== t('login.mfa_required')) {
            setMfaMessage(serverMsg);
          } else {
            setMfaMessage('');
          }
          setMfaMethod(response?.mfa_method || 'totp');
          setMfaCanResend(Boolean(response?.can_resend));
          setError('');
        } else {
          setMfaRequired(false);
          setError(response?.message || t('login.login_failed_check_credentials'));
        }
      } catch (err) {
        setMfaRequired(false);
        setError(err?.code === 'login_failed'
          ? t('login.login_failed_check_credentials')
          : err?.message || t('login.failed_send_try_again'));
      } finally {
        setIsLoading(false);
      }
    })();

    try {
      // If user wants to use a custom server, ensure it's applied and connected
  if (useCustomServer) {
        if (customServerInput && customServerInput.trim()) {
          // Apply the custom server and wait for connection
          setServerError('');
          setIsConnectingServer(true);
          applyCustomServer(persistServer);
          setPendingLogin(true);
          // Wait for the connection to establish; pendingLogin effect will send credentials
          return;
        }

        // If server is already set but socket is not open, wait for connection
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          // We rely on applyCustomServer to establish connection for custom servers
          setPendingLogin(true);
          setIsLoading(true);
          return;
        }
      }

      if (ws && ws.readyState === WebSocket.OPEN) {
        const loginData = {
          username: username.trim(),
          password: password
        };
        const loginMessage = {
          command: 'login',
          data: loginData
        };

        ws.send(JSON.stringify(loginMessage));
      } else {
        // No websocket open for the default server — connect now and send login when ready
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          setServerError('');
          setIsConnectingServer(true);
          try {
            connect();
            setPendingLogin(true);
            // Keep isLoading true until login_response
            return;
          } catch (e) {
            setError(t('login.failed_connect_server'));
            setIsLoading(false);
            setIsConnectingServer(false);
            return;
          }
        }

        setError(t('login.connection_lost'));
        setIsLoading(false);
      }
    } catch (err) {
      setError(t('login.failed_send_try_again'));
      setIsLoading(false);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // MFA dialog state
  const [mfaMessage, setMfaMessage] = useState('');
  const [mfaCanResend, setMfaCanResend] = useState(false);
  const [mfaMethod, setMfaMethod] = useState('totp');

  const handleCancelMfa = () => {
    setMfaRequired(false);
    setMfaMessage('');
    setMfaCanResend(false);
    setMfaMethod('totp');
    setError('');
  };

  // Called by MfaDialog when user submits a 6-digit code
  const onVerifyMfa = async (code) => {
    if (!code || code.length < 6) {
      setError(t('login.enter_mfa_code'));
      return;
    }
    setIsLoading(true);
    setError('');
    return (async () => {
      try {
        const response = await apiRequest('/api/v1/auth/login', {
          method: 'POST',
          body: JSON.stringify({
            username: username.trim(),
            password,
            mfa_code: code
          })
        });

        if (response?.success) {
          setUsername('');
          setPassword('');
          setMfaRequired(false);
          setError('');
          if (response.session_token) {
            setApiSessionToken(response.session_token);
          }
          localStorage.setItem('dataunfold_logged_in', 'true');
          if (response.mfa_setup_required) {
            localStorage.setItem('dataunfold_mfa_setup_required', 'true');
          } else {
            localStorage.removeItem('dataunfold_mfa_setup_required');
          }
          window.location.href = await afterSaasLoginPath();
          return;
        }

        if (response?.mfa_required) {
          setMfaRequired(true);
          const serverMsg = response?.message || '';
          if (serverMsg && serverMsg !== 'MFA code required' && serverMsg !== t('login.mfa_required')) {
            setMfaMessage(serverMsg);
          } else {
            setMfaMessage('');
          }
          setMfaMethod(response?.mfa_method || 'totp');
          setMfaCanResend(Boolean(response?.can_resend));
          setError('');
        } else {
          setError(response?.message || t('login.failed_send_try_again'));
        }
      } catch (err) {
        setError(err?.message || t('login.failed_send_try_again'));
      } finally {
        setIsLoading(false);
      }
    })();
    try {
      if (ws && ws.readyState === WebSocket.OPEN) {
        const loginData = { username: username.trim(), password, mfa_code: code };
        ws.send(JSON.stringify({ command: 'login', data: loginData }));
      } else {
        // If socket is not open, trigger connect + pending login flow
        setPendingLogin(true);
        connect();
      }
    } catch (err) {
      setError(t('login.failed_send_try_again'));
      setIsLoading(false);
    }
  };

  const handleResendMfa = () => {
    if (!mfaCanResend) {
      // Do nothing if server doesn't allow resend (e.g., TOTP)
      setError(t('login.resend_not_available') || 'Resend unavailable for this MFA method');
      return;
    }
    setError(t('login.resend_not_available') || 'Resend unavailable for this MFA method');
    return;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ command: 'resend_mfa', data: { username: username.trim() } }));
        setMfaMessage(t('login.mfa_resend_sent') || 'Verification code resent');
      } catch (e) {
        setError(t('login.failed_send_try_again'));
      }
    } else {
      setError(t('login.failed_connect_server'));
    }
  };

  const primaryColor = theme.palette.primary.main;
  const primaryLight = theme.palette.primary.light;
  

  // Convert a hex color to rgba string with alpha
  const hexToRgba = (hex, alpha = 1) => {
    if (!hex) return `rgba(0,0,0,${alpha})`;
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const bigint = parseInt(full, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Animations
  const float = keyframes`
    0% { transform: translateY(0px); }
    50% { transform: translateY(-8px); }
    100% { transform: translateY(0px); }
  `;

  const pulse = keyframes`
    0% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(0,0,0,0)); }
    50% { transform: scale(1.06) rotate(-1deg); filter: drop-shadow(0 0 18px ${hexToRgba(primaryLight, 0.42)}); }
    100% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(0,0,0,0)); }
  `;

  const starTwinkle = keyframes`
    0% { opacity: 0.15; transform: scale(0.9); }
    50% { opacity: 1; transform: scale(1.25); }
    100% { opacity: 0.15; transform: scale(0.9); }
  `;

  const scanline = keyframes`
    0% { background-position: 0 0; }
    100% { background-position: 0 300px; }
  `;

  const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  `;

  const drift = keyframes`
    0% { transform: translate(0px, 0px) scale(1); opacity: 0.9; }
    50% { transform: translate(8px, -8px) scale(1.05); opacity: 0.5; }
    100% { transform: translate(0px, 0px) scale(1); opacity: 0.9; }
  `;

  const workReveal = keyframes`
    0%, 8% { opacity: 0; transform: translateY(10px) scale(0.94); }
    17%, 48% { opacity: 0.92; transform: translateY(0) scale(1); }
    64%, 100% { opacity: 0; transform: translateY(-6px) scale(1.02); }
  `;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        paddingBottom: isSmall ? '96px' : '0px'
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={12}
          sx={{
            background: 'white',
            borderRadius: 6,
            p: 0,
            width: { xs: '100%', md: '880px' },
            mx: 'auto',
            boxShadow: `0px 20px 52px rgba(87, 87, 176, 0.22), 0px 10px 28px rgba(87, 87, 176, 0.14)`,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
            {/* Language switcher - page level */}
            <Box sx={{ position: 'absolute', top: 30, right: 30, zIndex: 1400 }}>
              <Button size="small" variant="outlined" onClick={openLanguageMenu} startIcon={<Language />} sx={{ textTransform: 'none', borderRadius: 3 }}>
                {i18n?.language?.toUpperCase?.() || 'EN'}
              </Button>
            </Box>
            <SidebarLanguageMenu anchorEl={languageAnchor} open={!!languageAnchor} onClose={closeLanguageMenu} onLanguageChange={handleLanguageChange} primaryColor={primaryColor} lightPurple={primaryLight} />
          <Grid container>
            {/* Left brand/illustration panel */}
             <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'flex' } }}>
               <Box sx={{
                 background: `linear-gradient(145deg, #302365 0%, ${primaryColor} 44%, ${primaryLight} 100%)`,
                 position: 'relative',
                 width: '100%',
                 p: 4,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                 color: '#fff',
                 minHeight: 320,
                 overflow: 'hidden',
               }}>
                 <Box
                   aria-hidden="true"
                   sx={{
                     position: 'absolute',
                     inset: 0,
                     background: `radial-gradient(circle at 50% 46%, ${hexToRgba(primaryLight, 0.24)} 0%, transparent 42%), radial-gradient(circle at 4% 100%, ${hexToRgba('#1d164c', 0.7)} 0%, transparent 48%)`,
                     pointerEvents: 'none',
                   }}
                 />
                 <Box
                   component="svg"
                   aria-hidden="true"
                   viewBox="0 0 520 400"
                   preserveAspectRatio="none"
                   sx={{
                     position: 'absolute',
                     inset: 0,
                     width: '100%',
                     height: '100%',
                     opacity: 0.92,
                     pointerEvents: 'none',
                   }}
                 >
                   <defs>
                     <linearGradient id="login-thread-gradient" x1="0" y1="0" x2="1" y2="1">
                       <stop offset="0%" stopColor="#c9b6ff" stopOpacity="0.1" />
                       <stop offset="48%" stopColor="#ffffff" stopOpacity="0.9" />
                       <stop offset="100%" stopColor="#7cf4ff" stopOpacity="0.4" />
                     </linearGradient>
                   </defs>
                    <g ref={networkCameraRef}>
                      <g>
                        {NETWORK_EDGES.map((edge) => (
                          <path
                            key={`completed-${edge.id}`}
                            ref={(node) => { completedNetworkPathRefs.current[edge.id] = node; }}
                            d={edge.d}
                            fill="none"
                            stroke="#d9d0ff"
                            strokeWidth="1.7"
                            strokeLinecap="round"
                            style={{ opacity: 0 }}
                          />
                        ))}
                      </g>
                      <g>
                        {NETWORK_EDGES.map((edge) => (
                          <path
                            key={`preview-${edge.id}`}
                            ref={(node) => { branchPreviewPathRefs.current[edge.id] = node; }}
                            d={edge.d}
                            fill="none"
                            stroke="#d9d0ff"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            style={{ opacity: 0 }}
                          />
                        ))}
                      </g>
                      <path
                        ref={activeNetworkPathRef}
                        d={NETWORK_EDGES[0].d}
                        fill="none"
                        stroke="#bffaff"
                        strokeWidth="3"
                        strokeLinecap="round"
                        style={{ opacity: prefersReducedMotion ? 0.72 : 0 }}
                      />
                      <circle ref={activeThreadGlowRef} cx="20" cy="340" r="12" fill="#7cf4ff" fillOpacity="0.2" />
                      <circle ref={activeThreadDotRef} cx="20" cy="340" r="4.5" fill="#e4ffff" fillOpacity="0.98" />
                    </g>
                  </Box>
                 {activeCheckpoint && (
                   <Box
                     key={activeCheckpoint.key}
                     aria-hidden="true"
                     sx={{
                       position: 'absolute',
                       left: activeCheckpoint.left,
                       top: activeCheckpoint.top,
                       minWidth: 116,
                       px: 1.25,
                       py: 0.8,
                       border: '1px solid rgba(226, 218, 255, 0.32)',
                       borderRadius: 1.5,
                       background: 'linear-gradient(135deg, rgba(31, 21, 81, 0.72), rgba(104, 79, 190, 0.46))',
                       boxShadow: '0 8px 24px rgba(24, 13, 67, 0.2), inset 0 1px 0 rgba(255,255,255,0.12)',
                       backdropFilter: 'blur(5px)',
                       zIndex: 1,
                       opacity: prefersReducedMotion ? 0.86 : 0,
                       animation: prefersReducedMotion ? 'none' : `${workReveal} 3.4s ease forwards`,
                     }}
                   >
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 0.25 }}>
                       <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#7cf4ff', boxShadow: '0 0 10px rgba(124, 244, 255, 0.9)' }} />
                       <Typography sx={{ fontSize: '0.6rem', letterSpacing: '0.12em', fontWeight: 700, color: 'rgba(255,255,255,0.72)', lineHeight: 1.1 }}>
                          {t('login.ai_work_label')}
                       </Typography>
                     </Box>
                       <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                         {activeCheckpoint.label}
                       </Typography>
                       <Typography sx={{ fontSize: '0.62rem', color: 'rgba(238, 233, 255, 0.72)', lineHeight: 1.3 }}>
                         {activeCheckpoint.detail}
                       </Typography>
                   </Box>
                 )}
                 <Box
                   component="img"
                   src={'/images/ai/dataunfold.png'}
                  alt="DataUnfold"
                  sx={{
                    width: 56,
                    height: 56,
                    mb: 1.5,
                   animation: prefersReducedMotion ? 'none' : `${float} 3.8s ease-in-out infinite, ${pulse} 2000ms ease-in-out infinite`,
                   transformOrigin: 'center',
                   zIndex: 2,
                   borderRadius: '8px',
                   transition: 'transform 160ms ease, box-shadow 160ms ease',
                 }}
                />
                 {/* Decorative ambient glows */}
                 {!isSmall && (
                   <>
                    <Box sx={{ position: 'absolute', top: 8, left: -28, width: 160, height: 160, borderRadius: '50%', background: `radial-gradient(circle at 20% 20%, ${hexToRgba(primaryLight, 0.12)}, rgba(255,255,255,0) 40%)`, animation: prefersReducedMotion ? 'none' : `${drift} 9s ease-in-out infinite`, zIndex: 0 }} />
                    <Box sx={{ position: 'absolute', bottom: -12, right: -34, width: 80, height: 80, borderRadius: '50%', background: `radial-gradient(circle at 80% 60%, ${hexToRgba(primaryLight, 0.06)}, rgba(255,255,255,0) 40%)`, animation: prefersReducedMotion ? 'none' : `${drift} 12s ease-in-out infinite`, zIndex: 0 }} />
                    {/* Add extra neon beam */}
                    <Box sx={{ position: 'absolute', left: -20, top: 40, width: 6, height: 260, borderRadius: 3, background: `linear-gradient(180deg, ${hexToRgba('#fff', 0.08)}, ${hexToRgba(primaryLight, 0.28)}, ${hexToRgba(primaryColor, 0.12)})`, transform: 'skewX(-10deg)', zIndex: 0, opacity: 0.6 }} />
                  </>
                )}
                 <Typography
                   variant="h5"
                   sx={{
                    fontWeight: 700,
                    textAlign: 'center',
                    mb: 1,
                    opacity: 0,
                    animation: prefersReducedMotion ? 'none' : `${fadeIn} 700ms ease forwards`,
                     zIndex: 2,
                    textShadow: `0 4px 20px ${hexToRgba(primaryLight, 0.42)}, 0 2px 8px ${hexToRgba(primaryLight, 0.12)}`,
                  }}
                >
                  {t('login.welcome_back')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'center',
                    opacity: 0,
                    animation: prefersReducedMotion ? 'none' : `${fadeIn} 600ms ease forwards`,
                     zIndex: 2,
                  }}
                >
                  {t('login.please_log_in')}
                </Typography>
                {/* Scanline overlay */}
                {!isSmall && (
                  <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.06, backgroundImage: 'repeating-linear-gradient(to bottom, rgba(255,255,255,0.06), rgba(255,255,255,0.06) 1px, transparent 1px, transparent 6px)', animation: prefersReducedMotion ? 'none' : `${scanline} 6s linear infinite` }} />
                )}
                {/* Neon twinkle stars */}
                {!isSmall && (
                  <>
                    {[
                      { left: '8%', top: '18%', size: 6, delay: '0s' },
                      { left: '24%', top: '10%', size: 8, delay: '0.4s' },
                      { left: '40%', top: '28%', size: 5, delay: '1s' },
                      { left: '68%', top: '14%', size: 7, delay: '0.7s' },
                      { left: '86%', top: '30%', size: 4, delay: '1.2s' },
                    ].map((s, i) => (
                      <Box key={i} sx={{ position: 'absolute', left: s.left, top: s.top, width: s.size, height: s.size, borderRadius: '50%', background: `linear-gradient(180deg, ${hexToRgba('#fff', 1)}, ${hexToRgba(primaryLight, 0.7)})`, boxShadow: `0 0 14px ${hexToRgba(primaryLight, 0.22)}`, transform: 'translateZ(0)', animation: prefersReducedMotion ? 'none' : `${starTwinkle} ${5 + (i % 3)}s ${s.delay} ease-in-out infinite`, zIndex: 0 }} />
                    ))}
                  </>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Box sx={{ p: isSmall ? 3 : 5 }}>
          {/* Header (visible on small screens) */}
              <Box sx={{ textAlign: { xs: 'center', md: 'left' }, mb: 3 }}>
                <Box sx={{ display: { xs: 'inline-flex', md: 'none' }, alignItems: 'center', justifyContent: 'center', width: 64, height: 64, borderRadius: '50%', backgroundColor: primaryLight + '20', mb: 2, border: `2px solid ${primaryColor}20` }}>
                  <Lock sx={{ fontSize: 32, color: primaryColor }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: primaryColor, mb: 0.5 }}>
                  {t('login.welcome_back')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>{t('login.please_log_in')}</Typography>
              </Box>

          {['session_expired', 'signed_out'].includes(loginNoticeReason) && !isUserLoggedIn && (
            <Alert severity={loginNoticeReason === 'session_expired' ? 'warning' : 'info'} sx={{ mb: 3, borderRadius: 2 }}>
              {t(`login.${loginNoticeReason}`)}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <Button component={RouterLink} to="/signup" fullWidth sx={{ mb: 2 }}>{t('saas.signup_link')}</Button>
          <Box
            component="div"
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
          >
            {/* Optional custom server toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <FormControlLabel
                control={<Switch checked={useCustomServer} onChange={(e) => {
                  const checked = e.target.checked;
                  setUseCustomServer(checked);
                  if (!checked) {
                    // Reset to default server when toggled off
                    setCustomServerInput('');
                    try { clearCustomServer(); } catch (e) { /* ignore */ }
                  }
                }} />}
                label={<Typography variant="body2" sx={{ fontSize: '0.9rem' }}>{t('login.connections_optional')}</Typography>}
              />
            </Box>

            {useCustomServer && (
                <Box sx={{ 
                p: isSmall ? 1.5 : 2, 
                borderRadius: 2, 
                border: `1px solid ${primaryColor}20`,
                backgroundColor: primaryLight + '08',
                boxShadow: '0 2px 8px rgba(87, 87, 176, 0.08)'
              }}>
                <Grid container spacing={isSmall ? 1.5 : 2}>
                  {/* Server input */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('login.websocket_server')}
                      placeholder={t('login.websocket_server_placeholder')}
                      value={customServerInput}
                      onChange={(e) => setCustomServerInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCustomServer(); } }}
                      size={isSmall ? 'small' : 'medium'}
                      disabled={isConnectingServer}
                      error={!!serverError}
                      helperText={serverError || t('login.current_server', { server: serverUrl })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Cloud sx={{ color: primaryColor }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: primaryColor,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: primaryColor,
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Action buttons */}
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color={isConnected && isTargetServerCurrent ? 'success' : 'primary'}
                      size="medium"
                      onClick={() => applyCustomServer()}
                      disabled={!customServerInput || isConnectingServer || (isConnected && isTargetServerCurrent)}
                      fullWidth
                      sx={{ minHeight: 44 }}
                    >
                      {isConnectingServer ? t('login.connecting') : (isConnected && isTargetServerCurrent ? t('login.connected') : t('login.connect'))}
                    </Button>
                  </Grid>

                  {/* Persist checkbox and status */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <FormControlLabel
                        control={<Checkbox checked={persistServer} onChange={(e) => setPersistServer(e.target.checked)} size="small" />}
                        label={<Typography variant="body2" sx={{ fontSize: isSmall ? '0.85rem' : '0.875rem' }}>{t('login.remember_for_next_time')}</Typography>}
                      />
                      {isConnectingServer ? (
                        <CircularProgress size={20} />
                      ) : (
                          <Chip 
                          label={isConnected ? t('login.connected') : t('login.disconnected')} 
                          color={isConnected ? 'success' : 'default'} 
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      )}
                    </Box>
                  </Grid>

                  {/* Pending login alert */}
                  {pendingLogin && (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        {isConnectingServer ? t('login.connecting_send_automatically') : t('login.waiting_send_automatically')}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
            {/* Username Field */}
              <TextField
              fullWidth
              size={isSmall ? 'small' : 'medium'}
              label={t('login.username')}
              variant="outlined"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleLogin();
                }
              }}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: primaryColor }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: primaryColor,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: primaryColor,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: primaryColor,
                },
              }}
            />

            {/* Password Field */}
            <TextField
              fullWidth
              size={isSmall ? 'small' : 'medium'}
              label={t('login.password')}
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleLogin();
                }
              }}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: primaryColor }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={toggleShowPassword}
                      disabled={isLoading}
                      sx={{ color: primaryColor }}
                      aria-label={showPassword ? t('login.hide_password') : t('login.show_password')}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: primaryColor,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: primaryColor,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: primaryColor,
                },
              }}
            />

            {/* MFA dialog extracted to `MfaDialog` component */}
            <MfaDialog
              open={mfaRequired}
              onClose={handleCancelMfa}
              onVerify={onVerifyMfa}
              onResend={handleResendMfa}
              canResend={mfaCanResend}
              method={mfaMethod}
              message={mfaMessage}
              isLoading={isLoading}
            />

            {/* Pending-login info for default server */}
            {pendingLogin && !useCustomServer && (
              <Alert severity="info" sx={{ mb: 1 }}>{t('login.connecting_will_send_when_connected')}</Alert>
            )}

            {/* Login Button */}
            <Button
              fullWidth
              type="button"
              onClick={handleLogin}
              variant="contained"
              size={isSmall ? 'medium' : 'large'}
              disabled={isLoading || !username.trim() || !password.trim()}
              sx={{
                backgroundColor: primaryColor,
                borderRadius: 2,
                py: isSmall ? 1 : 1.5,
                fontSize: isSmall ? '0.95rem' : '1rem',
                fontWeight: 600,
                textTransform: 'none',
                minHeight: isSmall ? 44 : 48,
                boxShadow: `0px 4px 12px rgba(87, 87, 176, 0.3)`,
                '&:hover': {
                  backgroundColor: '#4a4a9d',
                  boxShadow: `0px 6px 16px rgba(87, 87, 176, 0.4)`,
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  boxShadow: 'none',
                },
              }}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isLoading ? t('login.logging_in') : t('login.log_in')}
            </Button>
          </Box>

          {/* Footer (in-dialog info) */}
          <Box sx={{ textAlign: 'center', mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
            <Typography variant="body2" sx={{ color: '#888', fontSize: '0.85rem' }}>
              {t('login.secure_authentication_required')}
            </Typography>
          </Box>
        </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
      {/* Global bottom footer (outside the login dialog) - attached appearance */}
      <Box sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1200, pointerEvents: 'none' }}>
        <Box sx={{ backgroundColor: 'rgba(255,255,255,0.96)', borderTop: '1px solid #eee', py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, pointerEvents: 'auto' }}>
          <img
            src={'/images/ai/dataunfold.png'}
            alt="DataUnfold"
            style={{ width: 18, height: 18, objectFit: 'contain', opacity: 0.95 }}
          />
          <Typography variant="caption" sx={{ color: '#666', fontSize: '0.82rem', fontWeight: 500 }}>
            {t('login.powered_by_prefix')}&nbsp;
            <Link href="https://www.dataunfold.com" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 600, color: 'inherit' }}>
              dataunfold
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
